(ns lemonpi.content-function.shared.macro
  "Parse/serialize macros. Internal representation of macros
   changed with time. In the first place it was a keyword of :alias.selector (m0), but
   later on we extended the syntax (m1), so the internal representation was changed to
   a map. Because we save internal representation to the database, this module
   must be able to read the 'keyword' representation as well."
  (:require [clojure.string :as str]))

(defn parse-int
  [s]
  #?(:clj (Integer/parseInt s 10)
     :cljs (js/parseInt s 10)))

;; Internal representation must be repsesentable in JSON
;; because it is saved like that into the database.
(defn create-macro
  ([alias selector] (create-macro alias selector []))
  ([alias selector function]
   {:alias alias
    :selector (vec selector)
    :type "m1"
    :function function}))

(def alias-selector
  #"\$\{([-_a-zA-Z0-9]+)?\.([^:]+)(?:\:(.+))*\}")

(def selector-part
  #"([-_a-zA-Z0-9_]+)(?:\[(\d+)\])?")

(def fn-with-str-part
  #"([a-zA-Z-_]+)\((?:|\"(.*)\")\)")

(def fn-part
  #"([a-zA-Z-_]+)\((.*)\)")

(defn parse-fn-args
  [args-string]
  (->> (str/split args-string #"\,\ ?")
       (reduce (fn [acc v]
                 (when (some? acc)
                   (some->> (when (re-matches #"[0-9]+" v)
                              (parse-int v))
                            (conj acc))))
               [])))

(defn parse-fn-int-args
  [f]
  (when-let [[_ func args-string] (re-matches fn-part f)]
    (when-let [args (parse-fn-args args-string)]
      (concat [func] args))))

(defn parse-fn-string-args
  [f]
  (when-let [[_ func arg] (re-matches fn-with-str-part f)]
    [func arg]))

(defn parse-fn
  [f]
  (if (re-matches fn-with-str-part f)
    (parse-fn-string-args f)
    (parse-fn-int-args f)))

(defn- parse-selector
  [s]
  (->> (str/split s #"\.")
       (mapcat (fn [part]
                 (let [[_ prop idx] (re-matches selector-part part)]
                   (if (nil? idx)
                     [(keyword prop)]
                     [(keyword prop) (parse-int idx)]))))))

(defn parse-macro
  "Parses a macro. Returns nil if it is not valid."
  [m]
  (when-let [[_ alias-part selector-part f-part] (re-matches alias-selector m)]
    (let [selector (parse-selector selector-part)
          f (if f-part (parse-fn f-part) [])]
      (when (and (some? alias-part)
                 (not (some nil? selector)) f)
        (create-macro (keyword alias-part)
                      selector
                      f)))))

(defn macro?
  "Checks if a given term is a macro representation"
  [m]
  (or (keyword? m) ; Legacy representation
      (and (map? m) (= (:type m) "m1"))))

(defn macro-literal?
  "Checks if a given string is a macro. It does not validate the inner part,
   only checks for the aiter syntax."
  [m]
  (boolean (and (string? m)
                (re-matches #"\$\{.*?\}" m))))

(defn- dispatch
  [m]
  (if (keyword? m) :m0 :m1))

(defmulti serialize-macro-core dispatch)

(defmethod serialize-macro-core :m0
  [m]
  (name m))

(defmethod serialize-macro-core :m1
  [{:keys [alias selector function]}]
  (let [a-str (name alias)
        s-str (apply str (map #(if (keyword? %)
                                 (str "." (name %))
                                 (str "[" % "]")) selector))
        fn-str (when (seq function)
                 (str ":" (first function)
                      "(" (if (string? (second function))
                            (str "\"" (second function) "\"")
                            (str/join ", " (rest function)))
                      ")"))]
    (str a-str s-str fn-str)))

(defn serialize-macro
  [m]
  (str "${" (serialize-macro-core m) "}"))

(defmulti get-full-path dispatch)

(defmethod get-full-path :m0
  [m]
  (mapv keyword (str/split (name m) #"\.")))

(defmethod get-full-path :m1
  [{alias :alias selector :selector}]
  (into [alias] selector))

(defmulti upgrade-macro dispatch)

(defmethod upgrade-macro :m0
  [m]
  (let [[alias & selector] (get-full-path m)]
    (create-macro alias selector)))

(defmethod upgrade-macro :m1
  [m]
  m)

(defn macro-expr?
  [x]
  (and (sequential? x)
       (every? #(or (string? %)
                    (macro? %)) x)))

(defn parse-macro-expr
  "Parses a string that may contain macros. For example:

    \"Hello ${MACRO}!!\"

  will be converted to:

    [\"Hello \" MACROREP \"!!\"]"
  [expr]
  (if (empty? expr)
    [""]
    (->> (re-seq #?(:clj #"([\s\S]*?)(?:(\$\{.*?\})|\z)"
                    :cljs #"([\s\S]*?)(?:(\$\{.*?\})|$)")
                 expr)
         (mapcat (fn [[_ t m]]
                   (cond-> []
                     (seq t) (conj t)
                     (seq m) (conj (parse-macro m))))))))

(defn serialize-macro-expr
  "Serializes a series of terms that may cntain macros to string.
  For example:

    [\"Hello \" MACROREP \"!!\"]

  will be serialized to:

    \"Hello ${MACRO}!!\""
  [expr]
  (->> expr
       (map (fn [s]
              (if (macro? s)
                (serialize-macro s)
                s)))
       (str/join)))
